import { useEffect, useSyncExternalStore } from "react";
import { queryClient } from "@/lib/queryClient";

// Impersonation used to null the auth cache and soft-navigate. That painted
// "Admin Access Required", then the previous screen, then the compass, then
// the destination. We instead cover the viewport for the whole handoff and
// hard-navigate only after the cookie is set, keeping this flag so the next
// document holds the compass until the destination's primary data is ready.

const DEST_KEY = "veras-portal-transition";
/** Identifies the document that started the handoff. A later document
 * (the destination, or a 401 redirect to /login) will not match. */
export const PORTAL_TRANSITION_DOC_KEY = "veras-portal-transition-doc";
/** Failsafe once we are already on the destination. Never armed on the
 * source page — a slow impersonation POST must keep the compass up, and
 * clearing the flag early would also drop it for the next document. */
export const PORTAL_TRANSITION_TIMEOUT_MS = 15_000;

let memoryDest: string | null = null;
const documentId = Math.random().toString(36).slice(2);
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function normalizePath(path: string): string {
  const bare = path.split("?")[0]?.split("#")[0] ?? path;
  if (bare.length > 1 && bare.endsWith("/")) return bare.slice(0, -1);
  return bare;
}

function readDestination(): string | null {
  if (memoryDest) return memoryDest;
  try {
    const stored = sessionStorage.getItem(DEST_KEY);
    return stored ? normalizePath(stored) : null;
  } catch {
    return null;
  }
}

export function isPortalTransitionActive(): boolean {
  return readDestination() !== null;
}

function readDocumentId(): string | null {
  try {
    return sessionStorage.getItem(PORTAL_TRANSITION_DOC_KEY);
  } catch {
    return null;
  }
}

/** True only in the tab that called beginPortalTransition. A full navigation
 * loads a new module, so this is false on the destination and on any
 * unexpected landing such as /login. */
function transitionStartedHere(): boolean {
  const stored = readDocumentId();
  if (stored) return stored === documentId;
  return memoryDest !== null;
}

export function beginPortalTransition(destination: string) {
  const path = normalizePath(destination);
  memoryDest = path;
  try {
    sessionStorage.setItem(DEST_KEY, path);
    sessionStorage.setItem(PORTAL_TRANSITION_DOC_KEY, documentId);
  } catch {
    // Same-tab overlay still works; a reload just won't keep the hold.
  }
  emit();
}

export function endPortalTransition() {
  if (!isPortalTransitionActive()) return;
  memoryDest = null;
  try {
    sessionStorage.removeItem(DEST_KEY);
    sessionStorage.removeItem(PORTAL_TRANSITION_DOC_KEY);
  } catch {
    // ignore
  }
  emit();
}

export function hardNavigate(path: string) {
  window.location.assign(path);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return isPortalTransitionActive();
}

export function usePortalTransition(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

type AuthIdentity = { role?: string | null } | null;

function querySettled(key: readonly unknown[]): boolean {
  const state = queryClient.getQueryState(key);
  return state?.status === "success" || state?.status === "error";
}

/**
 * True once the compass can come down. Stays false on the page we left
 * (location hasn't reached `destination` yet) and, for investor/partner
 * handoffs, until that portal's main payload has settled so we don't
 * reveal a skeleton or an access gate in between.
 */
export function isPortalDestinationReady(location: string, user: AuthIdentity): boolean {
  const dest = readDestination();
  if (!dest) return true;
  if (normalizePath(location) !== dest) return false;

  if (dest === "/dashboard") {
    // Any non-admin identity (investors are role "user") waits for the
    // portal payload. No user, or a still-admin session, means the
    // sign-in / admin gate is the real screen — don't cover it forever.
    if (!user || user.role === "admin") return true;
    return querySettled(["/api/investor/dashboard"]);
  }

  if (dest === "/agent/dashboard") {
    const me = queryClient.getQueryState(["/api/agent/me"]);
    if (!me || me.status === "pending") return false;
    if (me.status === "error" || me.data == null) return true;
    const agent = me.data as {
      portalStatus?: string | null;
      isImpersonatingAgent?: boolean;
    };
    const showFullDashboard =
      agent.portalStatus === "approved" || agent.isImpersonatingAgent === true;
    if (!showFullDashboard) return true;
    return querySettled(["/api/agent/dashboard"]);
  }

  // Admin landings (view-as-admin, and every "Back to Admin" target).
  // Auth has already resolved by the time we ask; the shell is the
  // right admin. List pages fill in underneath that first paint.
  return true;
}

export function useReleasePortalTransitionWhenReady(
  location: string,
  user: AuthIdentity,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) return;
    let raf = 0;
    let cancelled = false;
    const releaseIfReady = () => {
      if (cancelled || !isPortalDestinationReady(location, user)) return;
      if (raf) cancelAnimationFrame(raf);
      // Let the destination commit its ready paint under the compass,
      // then drop the cover on the next frame so the skeleton never shows.
      raf = requestAnimationFrame(() => {
        if (cancelled) return;
        if (isPortalDestinationReady(location, user)) endPortalTransition();
      });
    };
    releaseIfReady();
    const unsubscribe = queryClient.getQueryCache().subscribe(releaseIfReady);
    const dest = readDestination();
    const onDestination = dest != null && normalizePath(location) === dest;
    const startedHere = transitionStartedHere();
    // A later document that is not the destination (global 401 → /login,
    // or any other off-target hard redirect) must drop the cover. The
    // source page does not: clearing the flag there would also drop it
    // for the document we are about to open.
    if (!startedHere && dest && !onDestination) {
      endPortalTransition();
    }
    const timer = onDestination
      ? window.setTimeout(() => {
          if (!cancelled) endPortalTransition();
        }, PORTAL_TRANSITION_TIMEOUT_MS)
      : 0;
    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      unsubscribe();
      if (timer) window.clearTimeout(timer);
    };
  }, [enabled, location, user]);
}
